import React from 'react';
import { useTranslation } from 'react-i18next';
import { ModalWrapper, ModalCloseButton } from '../common';
import { easyPayProviders } from '../../constants/uiConstants';

/**
 * 간편결제 수단 선택 모달.
 * 결제수단에서 '간편결제(simple)' 선택 후 결제 버튼을 누르면 열리고,
 * 5종(카카오·네이버·토스·애플·삼성)을 각 사의 공식 브랜드 컬러 버튼으로 노출한다.
 * (토스페이먼츠 "자체창 방식" 권장 패턴 — 가맹점이 브랜드 식별이 되는 선택 UI 제공)
 * 하나를 고르면 onSelect(providerId) 로 전달되고, providerId(simple_kakao 등)는
 * portone.js resolveChannel 에서 easyPayProvider 로 매핑된다.
 */
const PROVIDER_STYLE = {
  simple_kakao:   { bg: '#FEE500', fg: '#191919', border: '1px solid rgba(0,0,0,0.06)' },
  simple_naver:   { bg: '#03C75A', fg: '#FFFFFF' },
  simple_toss:    { bg: '#0064FF', fg: '#FFFFFF' },
  simple_apple:   { bg: '#000000', fg: '#FFFFFF' },
  simple_samsung: { bg: '#1428A0', fg: '#FFFFFF' },
};

// Apple Pay 노출 조건 (엄격 모드 — iPhone 등 iOS 기기 한정).
//   1) Apple 공식 가용성 API ApplePaySession.canMakePayments() = 실제 결제 가능 환경에서만 true
//      (데스크탑 Chrome·Android 등은 ApplePaySession 자체가 없어 false).
//   2) + iOS UA 조건 → macOS 사파리(애플페이 가능하지만)도 제외하고 iOS 기기에서만 노출.
//      (가맹점 정책: 아이폰 사파리에서만 결제 인지/가능 → 그 외엔 버튼 숨김)
function isApplePayAvailable() {
  try {
    if (typeof window === 'undefined') return false;
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const canMakePayments = !!(window.ApplePaySession
      && typeof window.ApplePaySession.canMakePayments === 'function'
      && window.ApplePaySession.canMakePayments());
    return isIOS && canMakePayments;
  } catch (_) {
    return false;
  }
}

export default function EasyPayProviderModal({ open, onClose, onSelect }) {
  const { t } = useTranslation();
  if (!open) return null;
  // 애플페이는 가용 환경에서만 노출 (그 외 기기에선 목록에서 제외).
  const providers = easyPayProviders.filter((p) => (p.id === 'simple_apple' ? isApplePayAvailable() : true));
  return (
    <ModalWrapper onClose={onClose} maxWidth="modal" gap="gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-mono-900">
          {t('payment.easypay_select_title', { defaultValue: '간편결제 수단 선택' })}
        </h2>
        <ModalCloseButton onClick={onClose} />
      </div>
      <div className="flex flex-col gap-2.5">
        {providers.map((p) => {
          const s = PROVIDER_STYLE[p.id] || { bg: '#FFFFFF', fg: '#111111' };
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              style={{ backgroundColor: s.bg, color: s.fg, border: s.border }}
              className="w-full h-[52px] rounded-xl text-base font-bold flex items-center justify-center transition active:scale-[0.99] hover:opacity-90"
            >
              {t(p.labelKey)}
            </button>
          );
        })}
      </div>
    </ModalWrapper>
  );
}
